import React, {
    useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';
import { toast } from 'react-toastify';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import AddPhotoAlternateRoundedIcon from '@mui/icons-material/AddPhotoAlternateRounded';
import { useChat } from '../../Context/ChatContext';
import { dayLabel } from '../../helpers/chatTime';
import { imagesFromTransfer, isSendableImage, prettySize } from '../../helpers/chatMedia';
import MessageBubble from './MessageBubble';
import ImageLightbox from './ImageLightbox';
import './Chat.scss';

/* The message list and the box you type in — the same component whether it is
   filling the chat page or sitting inside a popup, so the two can never drift
   apart in behaviour. */
const ChatThread = ({ conversationId, peer, compact = false }) => {
    const {
        myCode, threads, typingBy, sendMessage, retryMessage,
        editMessage, deleteMessage, uploadImage,
        loadThread, loadOlder, notifyTyping, stopTyping,
    } = useChat();

    const [draft, setDraft] = useState('');
    const [showJump, setShowJump] = useState(false);
    /* The message being answered, and the one being rewritten. Only ever one
       of the two: replying to something while editing something else would
       leave no way to say which the composer is about to act on. */
    const [replyingTo, setReplyingTo] = useState(null);
    const [editing, setEditing] = useState(null);
    /* The picture waiting to go with the next message: one at a time, so the
       composer stays a single line of intent rather than a staging area. */
    const [pending, setPending] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [lightbox, setLightbox] = useState(null);
    /* Briefly flashed after jumping to a quoted message, so the eye lands on
       the right bubble instead of somewhere in the middle of the thread. */
    const [highlightId, setHighlightId] = useState(null);

    const scrollRef = useRef(null);
    const bottomRef = useRef(null);
    const inputRef = useRef(null);
    const fileRef = useRef(null);
    /* Whether the reader is at the bottom. A message must not yank the view
       down while they are reading back through older ones. */
    const atBottomRef = useRef(true);
    const previousCountRef = useRef(0);
    const previousConversationRef = useRef(null);
    const highlightTimerRef = useRef(null);
    /* Which upload the composer is currently waiting on. An upload is slow
       enough that the sender can pick a picture, switch conversation and
       carry on typing before it lands — without this the finished upload
       would attach itself to whichever thread happens to be on screen. */
    const uploadTokenRef = useRef(0);
    /* Dragging over a child fires a leave for the parent, so the overlay is
       kept on a depth count rather than a flag — otherwise it flickers off
       every time the pointer crosses a bubble. */
    const dragDepthRef = useRef(0);

    const thread = threads[conversationId] || { messages: [], loading: false, hasMore: false };
    const { messages } = thread;
    const peerTyping = Boolean(peer && typingBy[peer.employeeCode]);

    useEffect(() => {
        if (conversationId) loadThread(conversationId);
    }, [conversationId, loadThread]);

    /* Everything the composer was holding belongs to the thread that was on
       screen. Carrying a half-written reply into a different conversation
       would send it to the wrong person. */
    useEffect(() => {
        uploadTokenRef.current += 1;
        setDraft('');
        setReplyingTo(null);
        setEditing(null);
        setPending(null);
        setLightbox(null);
    }, [conversationId]);

    useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

    const scrollToBottom = useCallback((behavior = 'smooth') => {
        const node = scrollRef.current;
        if (!node) return;
        node.scrollTo({ top: node.scrollHeight, behavior });
        atBottomRef.current = true;
        setShowJump(false);
    }, []);

    /* Kept in a layout effect so the jump happens in the same frame the
       messages are painted; in a plain effect the thread visibly starts at
       the top and then snaps down. */
    useLayoutEffect(() => {
        const node = scrollRef.current;
        if (!node) return;

        const switched = previousConversationRef.current !== conversationId;
        const grew = messages.length > previousCountRef.current;
        previousConversationRef.current = conversationId;
        previousCountRef.current = messages.length;

        if (switched) {
            node.scrollTop = node.scrollHeight;
            atBottomRef.current = true;
            setShowJump(false);
            return;
        }
        if (!grew) return;

        const last = messages[messages.length - 1];
        const mine = last && last.senderCode === myCode;

        if (mine || atBottomRef.current) {
            node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
        } else {
            /* Someone else wrote while the reader is further up — leave the
               view where it is and offer them the jump button instead. */
            setShowJump(true);
        }
    }, [messages, conversationId, myCode]);

    const onScroll = useCallback((event) => {
        const node = event.currentTarget;
        const distance = node.scrollHeight - node.scrollTop - node.clientHeight;
        atBottomRef.current = distance < 60;
        if (atBottomRef.current) setShowJump(false);

        /* Older messages load as the reader reaches the top. */
        if (node.scrollTop < 40 && thread.hasMore && !thread.loading) {
            const before = node.scrollHeight;
            loadOlder(conversationId).then(() => {
                /* Hold the reader's place: without this the page grows above
                   them and the message they were reading jumps off screen. */
                requestAnimationFrame(() => {
                    if (scrollRef.current) {
                        scrollRef.current.scrollTop = scrollRef.current.scrollHeight - before;
                    }
                });
            });
        }
    }, [conversationId, loadOlder, thread.hasMore, thread.loading]);

    /* ------------------------- pictures ------------------------- */

    const attach = useCallback(async (file) => {
        if (!file) return;
        if (!isSendableImage(file)) {
            toast.error('Only JPG, PNG, GIF or WebP images can be sent');
            return;
        }
        if (file.size > 8 * 1024 * 1024) {
            toast.error('Image must be 8MB or smaller');
            return;
        }

        /* Shown as soon as it is picked, with the upload running behind it,
           so choosing a picture feels immediate even on a slow line. */
        const staged = { name: file.name || 'Screenshot', size: file.size, progress: 0 };
        uploadTokenRef.current += 1;
        const token = uploadTokenRef.current;
        const current = () => uploadTokenRef.current === token;

        setPending(staged);

        const result = await uploadImage(file, (progress) => {
            if (current()) setPending((now) => (now ? { ...now, progress } : now));
        });

        /* Abandoned while it was going up — another picture was picked, or
           the sender moved to a different thread. The bytes are already on
           the server and will simply go unreferenced. */
        if (!current()) {
            if (result.previewUrl) URL.revokeObjectURL(result.previewUrl);
            return;
        }

        if (result.error) {
            setPending(null);
            toast.error(result.error);
            return;
        }
        setPending({
            ...staged,
            progress: 100,
            attachment: result.attachment,
            previewUrl: result.previewUrl,
        });
        inputRef.current?.focus();
    }, [uploadImage]);

    const clearPending = () => {
        if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
        setPending(null);
    };

    /* Ctrl/Cmd+V with a screenshot on the clipboard. Only intercepted when
       the paste actually carries an image, so pasting text still behaves as
       an ordinary paste into the box. */
    const onPaste = (event) => {
        const [image] = imagesFromTransfer(event.clipboardData);
        if (!image) return;
        event.preventDefault();
        attach(image);
    };

    const onDragEnter = (event) => {
        if (!Array.from(event.dataTransfer?.types || []).includes('Files')) return;
        dragDepthRef.current += 1;
        setDragging(true);
    };

    const onDragLeave = () => {
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (!dragDepthRef.current) setDragging(false);
    };

    const onDrop = (event) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setDragging(false);
        const [image] = imagesFromTransfer(event.dataTransfer);
        if (image) attach(image);
        else toast.error('Only images can be dropped into a chat');
    };

    /* ------------------------- replying and editing ------------------------- */

    const startReply = useCallback((message) => {
        setEditing(null);
        setReplyingTo(message);
        inputRef.current?.focus();
    }, []);

    const startEdit = useCallback((message) => {
        setReplyingTo(null);
        setEditing(message);
        setDraft(message.text || '');
        /* Focused on the next frame: the textarea is being re-rendered with
           the message in it, and a caret placed before that lands at the
           start of the old value instead of the end of the new one. */
        requestAnimationFrame(() => {
            const node = inputRef.current;
            if (!node) return;
            node.focus();
            node.setSelectionRange(node.value.length, node.value.length);
        });
    }, []);

    const cancelEdit = useCallback(() => {
        setEditing(null);
        setDraft('');
    }, []);

    const remove = useCallback(async (message) => {
        const result = await deleteMessage(conversationId, message);
        if (!result || !result.success) {
            toast.error(result?.message || 'That message could not be deleted');
        }
    }, [conversationId, deleteMessage]);

    const retry = useCallback(
        (message) => retryMessage(conversationId, peer.employeeCode, message),
        [conversationId, peer, retryMessage],
    );

    /* Jump to the message a quote points at. It may well be further back
       than the page of history that is loaded, which is why a miss says so
       rather than quietly doing nothing. */
    const jumpToQuote = useCallback((messageId) => {
        const node = scrollRef.current?.querySelector(`[data-mid="${messageId}"]`);
        if (!node) {
            toast.info('That message is further back — scroll up to load it');
            return;
        }
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightId(String(messageId));
        clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightId(null), 1600);
    }, []);

    /* ------------------------- sending ------------------------- */

    const submit = async (event) => {
        if (event) event.preventDefault();
        const text = draft.trim();

        if (editing) {
            if (text === (editing.text || '')) { cancelEdit(); return; }
            if (!text && !editing.attachment) {
                toast.error('Delete the message instead of clearing it');
                return;
            }
            const target = editing;
            cancelEdit();
            const result = await editMessage(conversationId, target, text);
            if (!result || !result.success) {
                toast.error(result?.message || 'That message could not be edited');
                /* Put the sender back where they were rather than losing what
                   they typed over an error they can do something about. */
                startEdit(target);
                setDraft(text);
            }
            return;
        }

        /* A picture still on its way has no server-side file to point at yet,
           so sending now would drop it. */
        if (pending && !pending.attachment) {
            toast.info('The image is still uploading');
            return;
        }
        if (!text && !pending) return;

        sendMessage(conversationId, peer.employeeCode, text, {
            attachment: pending?.attachment || null,
            previewUrl: pending?.previewUrl || '',
            replyTo: replyingTo,
        });

        setDraft('');
        setReplyingTo(null);
        /* Not revoked here: the blob is now what the sent bubble is showing
           until the stored copy has loaded in its place. */
        setPending(null);
        stopTyping(peer.employeeCode, conversationId);
        atBottomRef.current = true;
    };

    const onKeyDown = (event) => {
        /* Enter sends, Shift+Enter starts a new line — what people expect of
           a chat box rather than a form field. */
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
            return;
        }
        if (event.key === 'Escape') {
            if (editing) cancelEdit();
            else if (replyingTo) setReplyingTo(null);
            else if (pending) clearPending();
        }
    };

    const onChange = (event) => {
        setDraft(event.target.value);
        /* An edit is a correction to something already delivered, not a new
           message, so it should not set the other side's "typing…" going. */
        if (peer && !editing) notifyTyping(peer.employeeCode, conversationId);
    };

    const canSend = Boolean(draft.trim()) || Boolean(pending?.attachment);

    let lastDay = null;

    return (
        <div
            className={`chat-thread${compact ? ' chat-thread--compact' : ''}${dragging ? ' is-dropping' : ''}`}
            onDragEnter={onDragEnter}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
        >
            <div className="chat-thread-scroll" ref={scrollRef} onScroll={onScroll}>
                {thread.hasMore && (
                    <div className="chat-thread-older">
                        {thread.loading ? 'Loading earlier messages…' : 'Scroll up for earlier messages'}
                    </div>
                )}

                {!thread.loading && !messages.length && (
                    <div className="chat-thread-empty">
                        <span className="chat-thread-empty-mark">👋</span>
                        <p>Say hello to {peer?.employeeName || 'them'}</p>
                        <small>Messages are private between the two of you.</small>
                    </div>
                )}

                {messages.map((message, index) => {
                    const day = dayLabel(message.createdAt);
                    const showDay = day !== lastDay;
                    lastDay = day;

                    const previous = messages[index - 1];
                    /* Consecutive messages from the same person are grouped,
                       so a burst reads as one turn rather than a stack of
                       separately-timed bubbles. A reply starts its own turn:
                       it carries a quote above it that would otherwise be
                       tucked under the message before it. */
                    const grouped = previous
                        && previous.senderCode === message.senderCode
                        && !showDay
                        && !message.replyTo
                        && Math.abs(new Date(message.createdAt) - new Date(previous.createdAt)) < 120000;

                    return (
                        <React.Fragment key={message._id || message.clientId}>
                            {showDay && <div className="chat-day"><span>{day}</span></div>}
                            <MessageBubble
                                message={message}
                                mine={message.senderCode === myCode}
                                grouped={grouped}
                                myCode={myCode}
                                highlighted={highlightId === String(message._id)}
                                onReply={startReply}
                                onEdit={startEdit}
                                onDelete={remove}
                                onRetry={retry}
                                onOpenImage={setLightbox}
                                onJumpToQuote={jumpToQuote}
                            />
                        </React.Fragment>
                    );
                })}

                {peerTyping && (
                    <div className="chat-row">
                        <div className="chat-bubble chat-bubble--typing" aria-label={`${peer?.employeeName} is typing`}>
                            <i /><i /><i />
                        </div>
                    </div>
                )}

                <div ref={bottomRef} />
            </div>

            {showJump && (
                <button type="button" className="chat-jump" onClick={() => scrollToBottom()}>
                    <ArrowDownwardRoundedIcon /> New messages
                </button>
            )}

            {dragging && (
                <div className="chat-drop">
                    <AddPhotoAlternateRoundedIcon />
                    <b>Drop the image to send it</b>
                </div>
            )}

            <form className="chat-composer" onSubmit={submit}>
                {replyingTo && (
                    <div className="chat-context is-reply">
                        <ReplyRoundedIcon />
                        <span className="chat-context-text">
                            <b>
                                Replying to {replyingTo.senderCode === myCode
                                    ? 'yourself' : (peer?.employeeName || 'them')}
                            </b>
                            <em>
                                {replyingTo.text
                                    || (replyingTo.attachment ? 'Photo' : 'Message')}
                            </em>
                        </span>
                        <button type="button" onClick={() => setReplyingTo(null)} aria-label="Cancel reply">
                            <CloseRoundedIcon />
                        </button>
                    </div>
                )}

                {editing && (
                    <div className="chat-context is-edit">
                        <EditRoundedIcon />
                        <span className="chat-context-text">
                            <b>Editing message</b>
                            <em>Press Escape to leave it as it was</em>
                        </span>
                        <button type="button" onClick={cancelEdit} aria-label="Cancel edit">
                            <CloseRoundedIcon />
                        </button>
                    </div>
                )}

                {pending && (
                    <div className="chat-context is-image">
                        {pending.previewUrl
                            ? <img src={pending.previewUrl} alt="" className="chat-context-thumb" />
                            : <ImageRoundedIcon />}
                        <span className="chat-context-text">
                            <b>{pending.name}</b>
                            <em>
                                {pending.attachment
                                    ? prettySize(pending.size)
                                    : `Uploading… ${pending.progress}%`}
                            </em>
                            {!pending.attachment && (
                                <span className="chat-context-bar">
                                    <i style={{ width: `${pending.progress}%` }} />
                                </span>
                            )}
                        </span>
                        <button type="button" onClick={clearPending} aria-label="Remove image">
                            <CloseRoundedIcon />
                        </button>
                    </div>
                )}

                <div className="chat-composer-line">
                    {/* Hidden, and driven by the button beside it: a bare file
                        input cannot be styled to sit in a composer. */}
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp"
                        ref={fileRef}
                        hidden
                        onChange={(event) => {
                            attach(event.target.files?.[0]);
                            /* Reset, so choosing the same file twice in a row
                               still fires a change event. */
                            event.target.value = '';
                        }}
                    />
                    <button
                        type="button"
                        className="chat-attach"
                        onClick={() => fileRef.current?.click()}
                        disabled={Boolean(editing)}
                        title={editing ? 'Finish the edit first' : 'Send an image'}
                        aria-label="Attach an image"
                    >
                        <AddPhotoAlternateRoundedIcon />
                    </button>

                    <textarea
                        className="chat-composer-input"
                        ref={inputRef}
                        placeholder={editing
                            ? 'Edit your message…'
                            : `Message ${peer?.employeeName || ''}…`}
                        value={draft}
                        rows={1}
                        onChange={onChange}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        onBlur={() => peer && !editing && stopTyping(peer.employeeCode, conversationId)}
                    />

                    <button
                        type="submit"
                        className={`chat-send${editing ? ' is-saving' : ''}`}
                        disabled={!canSend && !editing}
                        aria-label={editing ? 'Save changes' : 'Send message'}
                    >
                        {editing ? <CheckRoundedIcon /> : <SendRoundedIcon />}
                    </button>
                </div>
            </form>

            <ImageLightbox message={lightbox} onClose={() => setLightbox(null)} />
        </div>
    );
};

export default ChatThread;
